/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/pages/Identities.tsx
 * DESCRIPTION: Comprehensive file analysis results page for media 
 *              verification. Displays detailed metadata, trust scores,
 *              forensic analysis, and verification chain for selected
 *              media assets.
 *
 * FEATURES:
 * - Video/image preview with metadata
 * - Identity card with QR code and trust score
 * - Tabbed analysis sections (Summary, Advanced, Forensics)
 * - Forensic details accordion
 * - Quick actions panel (fully functional with mock data)
 * - Copy-to-clipboard for all hash values
 * - License-based feature access (Enterprise only for Advanced/Forensics)
 * - User profile integration (via Header)
 * - Dark/Light mode support (via Header)
 *
 * DATA INTEGRATION:
 * - Mock data structured for easy replacement with API calls
 * - All UI states functional (loading, empty, error)
 * - Action handlers prepared for backend integration
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.0.0
 * @created 2026-03-26
 * @updated 2026-03-26 - Removed header code, reorganized layout with tabs,
 *                       fixed preview card width, made Quick Actions functional,
 *                       added license-based feature access
 * ─────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect } from 'react';
import { useUser, useClerk } from '@clerk/react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Bell,
  ChevronDown,
  LogOut,
  User,
  Settings,
  Copy,
  Check,
  RefreshCw,
  Shield,
  Archive,
  Download,
  Share2,
  MapPin,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileVideo,
  Fingerprint,
  Brain,
  Link,
  ChevronRight,
  ChevronDown as ChevronDownIcon,
  QrCode,
  Eye,
  EyeOff,
  Lock,
  Play,
  Tag,
  Calendar,
  HardDrive,
  Activity,
  LockKeyhole,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────
interface FileMetadata {
  filename: string;
  size: string;
  duration: string;
  resolution: string;
  format: string;
  uploadDate: string;
  sha256Hash: string;
  thumbnail: string;
  type: 'video' | 'image';
}

interface IdentityData {
  verimarkId: string;
  persistentId: string;
  persistentQR: string;
  trustScore: number;
  origin: 'original' | 'duplicate';
  watermarkVisible: boolean;
  watermarkInvisible: boolean;
}

interface EXIFData {
  exifLevel: number;
  exifType: string;
  size: string;
  sizeInternal: string;
  duration: string;
  resolution: string;
  format: string;
  uploadDate: string;
  creotype: string;
}

interface ContainerInfo {
  creationTools: string[];
  creationSoftware: string;
  gps?: {
    lat: number;
    lng: number;
    location: string;
  };
}

interface SimilarityMatch {
  id: string;
  filename: string;
  similarity: number;
  thumbnail: string;
  date: string;
}

interface AIAssessment {
  riskScore: number;
  riskLevel: 'low' | 'medium' | 'high';
  categories: string[];
  recommendations: string[];
  fullResponse: string;
}

interface VerificationEvent {
  id: string;
  timestamp: string;
  event: string;
  status: 'success' | 'warning' | 'error';
  details: string;
}

interface ForensicData {
  magicBytes: string;
  hexPatterns: string;
  anomalies: Array<{
    type: string;
    severity: 'low' | 'medium' | 'high';
    description: string;
  }>;
  creationSoftware: string;
  watermarkVisible: boolean;
  watermarkInvisible: boolean;
}

interface UserLicense {
  tier: 'free' | 'business' | 'enterprise';
}

// ─────────────────────────────────────────────────────────────────────
// MOCK DATA
// ─────────────────────────────────────────────────────────────────────
const MOCK_FILE_METADATA: FileMetadata = {
  filename: 'VeriMark_Intro_Video_01.mp4',
  size: '45MB',
  duration: '03:12',
  resolution: '1920x1080',
  format: 'MP4',
  uploadDate: '2026-03-28',
  sha256Hash: 'abc123de8e16129652703298e3f2375...',
  thumbnail: 'https://images.unsplash.com/photo-1536240478700-b869070f9279?w=800&h=450&fit=crop',
  type: 'video',
};

const MOCK_IDENTITY_DATA: IdentityData = {
  verimarkId: 'VM7381A',
  persistentId: 'VM7381A-PERSISTENT',
  persistentQR: 'https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=VM7381A',
  trustScore: 0.85,
  origin: 'original',
  watermarkVisible: true,
  watermarkInvisible: true,
};

const MOCK_EXIF_DATA: EXIFData = {
  exifLevel: 0,
  exifType: '1920x1080',
  size: '45MB',
  sizeInternal: '3clx',
  duration: '03:12',
  resolution: '190px',
  format: 'Hbxing MP4',
  uploadDate: '28 March 2026',
  creotype: '2026',
};

const MOCK_CONTAINER_INFO: ContainerInfo = {
  creationTools: ['AI', 'MP4'],
  creationSoftware: 'Adobe Premiere Pro 2024',
  gps: {
    lat: -43.36855068,
    lng: 38.35555232,
    location: 'Gulf of Mexico',
  },
};

const MOCK_SIMILARITY_MATCHES: SimilarityMatch[] = [
  {
    id: '1',
    filename: 'VeriMark_Intro_Video_02.mp4',
    similarity: 0.94,
    thumbnail: 'https://images.unsplash.com/photo-1536240478700-b869070f9279?w=200&h=150&fit=crop',
    date: '2026-03-27',
  },
  {
    id: '2',
    filename: 'Marketing_Campaign_Draft.mp4',
    similarity: 0.87,
    thumbnail: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=200&h=150&fit=crop',
    date: '2026-03-25',
  },
  {
    id: '3',
    filename: 'Product_Launch_Final.mp4',
    similarity: 0.72,
    thumbnail: 'https://images.unsplash.com/photo-1574717436401-063d29b06555?w=200&h=150&fit=crop',
    date: '2026-03-20',
  },
];

const MOCK_AI_ASSESSMENT: AIAssessment = {
  riskScore: 0.15,
  riskLevel: 'low',
  categories: ['Authentic Content', 'No Manipulation Detected', 'Valid Metadata'],
  recommendations: [
    'Content appears authentic with high confidence',
    'No signs of deepfake or manipulation detected',
    'Metadata is consistent and complete',
  ],
  fullResponse: 'The analyzed media file shows strong indicators of authentic origin. EXIF data is consistent with the reported creation tools. No manipulation artifacts were detected in the visual or audio streams. The watermark signatures are valid and match the expected pattern for this organization.',
};

const MOCK_VERIFICATION_EVENTS: VerificationEvent[] = [
  {
    id: '1',
    timestamp: '2026-03-28 10:30:00',
    event: 'File Uploaded',
    status: 'success',
    details: 'File received and queued for analysis',
  },
  {
    id: '2',
    timestamp: '2026-03-28 10:30:15',
    event: 'Metadata Extraction',
    status: 'success',
    details: 'EXIF and container metadata extracted successfully',
  },
  {
    id: '3',
    timestamp: '2026-03-28 10:30:45',
    event: 'Watermark Verification',
    status: 'success',
    details: 'Both visible and invisible watermarks detected and verified',
  },
  {
    id: '4',
    timestamp: '2026-03-28 10:31:30',
    event: 'AI Assessment',
    status: 'success',
    details: 'AI analysis completed with low risk score',
  },
  {
    id: '5',
    timestamp: '2026-03-28 10:32:00',
    event: 'Identity Assigned',
    status: 'success',
    details: 'Persistent ID VM7381A assigned to file',
  },
];

const MOCK_FORENSIC_DATA: ForensicData = {
  magicBytes: 'abc123de8e16129652703298e3f2375...',
  hexPatterns: 'dde: 02 00 00 00 00 00 00 00 00 00 00 00 00 00 00 00 ...',
  anomalies: [
    {
      type: 'Minor Metadata Inconsistency',
      severity: 'low',
      description: 'Creation date differs from upload date by 2 days',
    },
  ],
  creationSoftware: 'Adobe Premiere Pro 2024 v24.1.0',
  watermarkVisible: true,
  watermarkInvisible: true,
};

// Mock user license - Change this to test different tiers
const MOCK_USER_LICENSE: UserLicense = {
  tier: 'enterprise', // 'free' | 'business' | 'enterprise'
};

// ─────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────
const getTrustScoreColor = (score: number) => {
  if (score >= 0.7) return 'text-emerald-600 dark:text-emerald-400';
  if (score >= 0.4) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
};

const getTrustScoreBgColor = (score: number) => {
  if (score >= 0.7) return 'stroke-emerald-600';
  if (score >= 0.4) return 'stroke-yellow-600';
  return 'stroke-red-600';
};

const getOriginBadgeColor = (origin: string) => {
  if (origin === 'original') {
    return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300';
  }
  return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300';
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateString: string) => {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ─────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────
const Identities: React.FC = () => {
  // ───────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────
  const { user } = useUser();
  const { signOut } = useClerk();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'summary' | 'advanced' | 'forensics'>('summary');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['magic-bytes']));
  const [isLoading, setIsLoading] = useState(true);
  const [quickActionLoading, setQuickActionLoading] = useState<string | null>(null);

  // Data state
  const [fileMetadata, setFileMetadata] = useState<FileMetadata | null>(null);
  const [identityData, setIdentityData] = useState<IdentityData | null>(null);
  const [userLicense, setUserLicense] = useState<UserLicense>(MOCK_USER_LICENSE);

  // ───────────────────────────────────────────────────────────────────
  // EFFECTS
  // ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 800));
      setFileMetadata(MOCK_FILE_METADATA);
      setIdentityData(MOCK_IDENTITY_DATA);
      setIsLoading(false);
    };
    loadData();
  }, [id]);

  // ───────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────
  const handleSignOut = () => {
    signOut(() => {
      window.location.href = '/sign-in';
    });
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const handleQuickAction = async (action: string) => {
    setQuickActionLoading(action);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    switch (action) {
      case 're-analyze':
        alert('Re-analysis initiated! You will be notified when complete.');
        break;
      case 'deep-fake-check':
        alert('Deep fake check requested. Results will be available in 5-10 minutes.');
        break;
      case 'archive':
        alert('File archived successfully.');
        break;
      case 'download':
        alert('Download started: ' + fileMetadata?.filename);
        break;
      case 'share':
        const shareLink = `${window.location.origin}/identities/${id}`;
        navigator.clipboard.writeText(shareLink);
        alert('Share link copied to clipboard!');
        break;
    }
    
    setQuickActionLoading(null);
  };

  const isEnterprise = userLicense.tier === 'enterprise';

  // ───────────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-80 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="grid grid-cols-3 gap-6">
            <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!fileMetadata || !identityData) {
    return (
      <div className="p-8">
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            File Not Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The requested file could not be found or has been removed.
          </p>
          <button
            onClick={() => navigate('/library')}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
          >
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Page Title & Breadcrumb */}
      <div className="mb-6">
        <nav className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
          <span
            className="hover:text-gray-900 dark:hover:text-white cursor-pointer"
            onClick={() => navigate('/dashboard')}
          >
            Home
          </span>
          <span>/</span>
          <span
            className="hover:text-gray-900 dark:hover:text-white cursor-pointer"
            onClick={() => navigate('/library')}
          >
            Library
          </span>
          <span>/</span>
          <span className="font-medium text-gray-900 dark:text-white truncate">
            {fileMetadata.filename}
          </span>
        </nav>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column (70%) - File Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Main Display Card with Tabs */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Tab Navigation */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex">
                <button
                  onClick={() => setActiveTab('summary')}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'summary'
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <FileVideo className="w-4 h-4" />
                  Summary
                </button>
                <button
                  onClick={() => isEnterprise && setActiveTab('advanced')}
                  disabled={!isEnterprise}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    !isEnterprise
                      ? 'opacity-50 cursor-not-allowed text-gray-400'
                      : activeTab === 'advanced'
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <Brain className="w-4 h-4" />
                  Advanced
                  {!isEnterprise && (
                    <Lock className="w-3 h-3 ml-1" />
                  )}
                </button>
                <button
                  onClick={() => isEnterprise && setActiveTab('forensics')}
                  disabled={!isEnterprise}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    !isEnterprise
                      ? 'opacity-50 cursor-not-allowed text-gray-400'
                      : activeTab === 'forensics'
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <Fingerprint className="w-4 h-4" />
                  Forensics
                  {!isEnterprise && (
                    <Lock className="w-3 h-3 ml-1" />
                  )}
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {/* SUMMARY TAB */}
              {activeTab === 'summary' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Video/Image Preview */}
                  <div className="relative aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                    <img
                      src={fileMetadata.thumbnail}
                      alt={fileMetadata.filename}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = fileMetadata.type === 'video'
                          ? 'https://via.placeholder.com/800x450/1a365d/ffffff?text=Video+Preview'
                          : 'https://via.placeholder.com/800x450/1a365d/ffffff?text=Image+Preview';
                      }}
                    />
                    {fileMetadata.type === 'video' && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <button className="w-16 h-16 bg-white/90 dark:bg-gray-800/90 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                          <Play className="w-7 h-7 text-gray-900 dark:text-white ml-1" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* File Metadata */}
                  <div className="space-y-4">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        {fileMetadata.filename}
                      </h2>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                            <HardDrive className="w-4 h-4" /> Size
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">{fileMetadata.size}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                            <Clock className="w-4 h-4" /> Duration
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">{fileMetadata.duration}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                            <Activity className="w-4 h-4" /> Resolution
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">{fileMetadata.resolution}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                            <FileVideo className="w-4 h-4" /> Format
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">{fileMetadata.format}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                            <Calendar className="w-4 h-4" /> Upload Date
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">{formatDate(fileMetadata.uploadDate)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                            <Fingerprint className="w-4 h-4" /> SHA256 Hash
                          </p>
                          <div className="flex items-center gap-2">
                            <p className="font-mono text-xs text-gray-900 dark:text-white truncate">
                              {fileMetadata.sha256Hash}
                            </p>
                            <button
                              onClick={() => copyToClipboard(fileMetadata.sha256Hash, 'sha256')}
                              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                            >
                              {copiedField === 'sha256' ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <Copy className="w-4 h-4 text-gray-400" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ADVANCED TAB */}
              {activeTab === 'advanced' && isEnterprise && (
                <div className="space-y-6">
                  {/* Metadata Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Metadata</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">EXIF Data</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">EXIF Level</span>
                            <span className="font-medium text-gray-900 dark:text-white">{MOCK_EXIF_DATA.exifLevel}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">EXIF Type</span>
                            <span className="font-medium text-gray-900 dark:text-white">{MOCK_EXIF_DATA.exifType}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Size</span>
                            <span className="font-medium text-gray-900 dark:text-white">{MOCK_EXIF_DATA.size}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Duration</span>
                            <span className="font-medium text-gray-900 dark:text-white">{MOCK_EXIF_DATA.duration}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Container Info</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Creation Tools</span>
                            <span className="font-medium text-gray-900 dark:text-white">{MOCK_CONTAINER_INFO.creationTools.join(', ')}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Creation Software</span>
                            <span className="font-medium text-gray-900 dark:text-white">{MOCK_CONTAINER_INFO.creationSoftware}</span>
                          </div>
                          {MOCK_CONTAINER_INFO.gps && (
                            <div>
                              <span className="text-gray-600 dark:text-gray-400 flex items-center gap-2">
                                <MapPin className="w-4 h-4" /> GPS Location
                              </span>
                              <p className="font-medium text-gray-900 dark:text-white mt-1">{MOCK_CONTAINER_INFO.gps.location}</p>
                              <p className="text-xs font-mono text-gray-600 dark:text-gray-400">
                                {MOCK_CONTAINER_INFO.gps.lat}, {MOCK_CONTAINER_INFO.gps.lng}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Fingerprint Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Fingerprint</h3>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <div className="grid grid-cols-8 gap-1 mb-4">
                        {[...Array(64)].map((_, i) => (
                          <div
                            key={i}
                            className="aspect-square bg-gradient-to-br from-blue-500 to-purple-600 rounded"
                            style={{ opacity: 0.3 + (Math.random() * 0.7) }}
                          />
                        ))}
                      </div>
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Similarity Matches</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {MOCK_SIMILARITY_MATCHES.map((match) => (
                          <div key={match.id} className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                            <div className="aspect-video bg-gray-200 dark:bg-gray-600">
                              <img src={match.thumbnail} alt={match.filename} className="w-full h-full object-cover" />
                            </div>
                            <div className="p-3">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{match.filename}</p>
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{(match.similarity * 100).toFixed(0)}% match</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* AI Assessment Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">AI Assessment</h3>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Risk Score</span>
                        <span className={`text-lg font-bold ${getTrustScoreColor(MOCK_AI_ASSESSMENT.riskScore)}`}>
                          {(MOCK_AI_ASSESSMENT.riskScore * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {MOCK_AI_ASSESSMENT.categories.map((category, idx) => (
                          <span key={idx} className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-full text-sm">
                            {category}
                          </span>
                        ))}
                      </div>
                      <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-900 dark:text-white">{MOCK_AI_ASSESSMENT.fullResponse}</p>
                      </div>
                    </div>
                  </div>

                  {/* Verification Chain Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Verification Chain</h3>
                    <div className="space-y-4">
                      {MOCK_VERIFICATION_EVENTS.map((event, idx) => (
                        <div key={event.id} className="flex items-start gap-4">
                          <div className="relative">
                            <div className={`w-3 h-3 rounded-full ${
                              event.status === 'success' ? 'bg-emerald-500' :
                              event.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                            }`} />
                            {idx < MOCK_VERIFICATION_EVENTS.length - 1 && (
                              <div className="absolute top-3 left-1.5 w-0.5 h-12 bg-gray-200 dark:bg-gray-700" />
                            )}
                          </div>
                          <div className="flex-1 pb-4">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">{event.event}</p>
                              <span className="text-xs text-gray-600 dark:text-gray-400">{formatDateTime(event.timestamp)}</span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{event.details}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* FORENSICS TAB */}
              {activeTab === 'forensics' && isEnterprise && (
                <div className="space-y-4">
                  {/* Magic Bytes */}
                  <div>
                    <button
                      onClick={() => toggleSection('magic-bytes')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <span className="text-sm font-medium text-gray-900 dark:text-white">Magic Bytes</span>
                      <ChevronDownIcon className={`w-5 h-5 text-gray-600 dark:text-gray-400 transition-transform ${
                        expandedSections.has('magic-bytes') ? 'rotate-180' : ''
                      }`} />
                    </button>
                    {expandedSections.has('magic-bytes') && (
                      <div className="mt-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-600 dark:text-gray-400">Hash</span>
                          <button
                            onClick={() => copyToClipboard(MOCK_FORENSIC_DATA.magicBytes, 'magic-bytes')}
                            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                          >
                            {copiedField === 'magic-bytes' ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                        <p className="font-mono text-xs text-gray-900 dark:text-white break-all">{MOCK_FORENSIC_DATA.magicBytes}</p>
                      </div>
                    )}
                  </div>

                  {/* Hex Patterns */}
                  <div>
                    <button
                      onClick={() => toggleSection('hex-patterns')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <span className="text-sm font-medium text-gray-900 dark:text-white">Hex Patterns</span>
                      <ChevronDownIcon className={`w-5 h-5 text-gray-600 dark:text-gray-400 transition-transform ${
                        expandedSections.has('hex-patterns') ? 'rotate-180' : ''
                      }`} />
                    </button>
                    {expandedSections.has('hex-patterns') && (
                      <div className="mt-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <p className="font-mono text-xs text-gray-900 dark:text-white break-all">{MOCK_FORENSIC_DATA.hexPatterns}</p>
                      </div>
                    )}
                  </div>

                  {/* Anomalies Detected */}
                  <div>
                    <button
                      onClick={() => toggleSection('anomalies')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Anomalies Detected ({MOCK_FORENSIC_DATA.anomalies.length})
                      </span>
                      <ChevronDownIcon className={`w-5 h-5 text-gray-600 dark:text-gray-400 transition-transform ${
                        expandedSections.has('anomalies') ? 'rotate-180' : ''
                      }`} />
                    </button>
                    {expandedSections.has('anomalies') && (
                      <div className="mt-2 space-y-3">
                        {MOCK_FORENSIC_DATA.anomalies.map((anomaly, idx) => (
                          <div
                            key={idx}
                            className={`p-4 rounded-lg border-l-4 ${
                              anomaly.severity === 'high'
                                ? 'bg-red-50 dark:bg-red-900/20 border-red-500'
                                : anomaly.severity === 'medium'
                                ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500'
                                : 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <AlertTriangle className={`w-4 h-4 ${
                                anomaly.severity === 'high' ? 'text-red-600' :
                                anomaly.severity === 'medium' ? 'text-yellow-600' : 'text-blue-600'
                              }`} />
                              <span className="text-sm font-medium text-gray-900 dark:text-white">{anomaly.type}</span>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                anomaly.severity === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                                anomaly.severity === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' :
                                'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                              }`}>
                                {anomaly.severity.toUpperCase()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{anomaly.description}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Watermark Status (moved from main area) */}
                  <div>
                    <button
                      onClick={() => toggleSection('watermark')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <span className="text-sm font-medium text-gray-900 dark:text-white">Watermark Status</span>
                      <ChevronDownIcon className={`w-5 h-5 text-gray-600 dark:text-gray-400 transition-transform ${
                        expandedSections.has('watermark') ? 'rotate-180' : ''
                      }`} />
                    </button>
                    {expandedSections.has('watermark') && (
                      <div className="mt-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            {MOCK_FORENSIC_DATA.watermarkVisible ? (
                              <CheckCircle className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <AlertTriangle className="w-4 h-4 text-gray-400" />
                            )}
                            <span className="text-sm text-gray-900 dark:text-white">Visible Watermark</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {MOCK_FORENSIC_DATA.watermarkInvisible ? (
                              <CheckCircle className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <AlertTriangle className="w-4 h-4 text-gray-400" />
                            )}
                            <span className="text-sm text-gray-900 dark:text-white">Invisible Watermark</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* License Upgrade Prompt */}
              {!isEnterprise && (activeTab === 'advanced' || activeTab === 'forensics') && (
                <div className="text-center py-12">
                  <LockKeyhole className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Enterprise Feature
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Advanced analysis and forensic details are only available on Enterprise plans.
                  </p>
                  <button
                    onClick={() => navigate('/billing')}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
                  >
                    Upgrade to Enterprise
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Identity Cards Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Identity Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Identity Card</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">VeriMark-ID</p>
                  <p className="font-mono text-sm font-medium text-gray-900 dark:text-white">{identityData.verimarkId}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Persistent ID</p>
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-sm text-gray-900 dark:text-white truncate">{identityData.persistentId}</p>
                    <button
                      onClick={() => copyToClipboard(identityData.persistentId, 'persistent-id')}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                      {copiedId === 'persistent-id' ? (
                        <Check className="w-3 h-3 text-green-600" />
                      ) : (
                        <Copy className="w-3 h-3 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Persistent QR</p>
                  <div className="mt-2 bg-white p-2 rounded border border-gray-200 dark:border-gray-700 inline-block">
                    <QrCode className="w-20 h-20 text-gray-900 dark:text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* Trust Score */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Trust Score</h3>
              <div className="flex items-center justify-center">
                <div className="relative w-32 h-32">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="none" className="text-gray-200 dark:text-gray-700" />
                    <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="none" strokeDasharray={`${identityData.trustScore * 351.86} 351.86`} strokeLinecap="round" className={getTrustScoreBgColor(identityData.trustScore)} />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <p className={`text-3xl font-bold ${getTrustScoreColor(identityData.trustScore)}`}>{identityData.trustScore.toFixed(2)}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">/1.0</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Creation Software (moved from Forensics) */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Creation Software</h3>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <FileVideo className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{MOCK_FORENSIC_DATA.creationSoftware}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Detected from metadata</p>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Origin Badge</h4>
                <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getOriginBadgeColor(identityData.origin)}`}>
                  {identityData.origin === 'original' ? 'Original Content' : 'Duplicate Detected'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column (30%) - Quick Actions */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 sticky top-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => handleQuickAction('re-analyze')}
                disabled={quickActionLoading === 're-analyze'}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {quickActionLoading === 're-analyze' ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <RefreshCw className="w-5 h-5" />
                )}
                Re-Analyze
              </button>
              <button
                onClick={() => handleQuickAction('deep-fake-check')}
                disabled={quickActionLoading === 'deep-fake-check'}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {quickActionLoading === 'deep-fake-check' ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Shield className="w-5 h-5" />
                )}
                Request Deep Fake Check
              </button>
              <button
                onClick={() => handleQuickAction('archive')}
                disabled={quickActionLoading === 'archive'}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {quickActionLoading === 'archive' ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Archive className="w-5 h-5" />
                )}
                Archive
              </button>
              <button
                onClick={() => handleQuickAction('download')}
                disabled={quickActionLoading === 'download'}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {quickActionLoading === 'download' ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Download className="w-5 h-5" />
                )}
                Download Original
              </button>
              <button
                onClick={() => handleQuickAction('share')}
                disabled={quickActionLoading === 'share'}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {quickActionLoading === 'share' ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Share2 className="w-5 h-5" />
                )}
                Share Link
              </button>
            </div>

            {/* License Info */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Current Plan</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{userLicense.tier}</span>
              </div>
              {!isEnterprise && (
                <button
                  onClick={() => navigate('/billing')}
                  className="w-full mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Upgrade to Enterprise
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Identities;
